import { useCallback, useEffect, useMemo, useState } from "react";
import { nf, ago, card, control, sectionLabel, Chip } from "../kb/shared";
import { parseCommunityHash } from "../create/route";

// Community Intel (#/community): people sign in with GitHub and submit OSINT (an article's
// link, a STIX bundle or an IOC list). The server (community.js) checks it by rules and
// publishes what passes within minutes as a Crowdsourced Report. Tabs: Crowdsourced
// Reports, Submit, My OSINT, and Review for the operator.

const TABS = [["reports", "Crowdsourced Reports"], ["submit", "Submit"], ["mine", "My OSINT"], ["review", "Review"]];
const KIND_LABEL = { url: "Link", stix: "STIX bundle", iocs: "IOC list" };
const TIER_LABEL = { new: "New contributor", established: "Established contributor", trusted: "Trusted contributor", banned: "Banned" };
const STATUS = {
  queued: ["Waiting", "#a1a1aa"], checking: ["Checking", "#0ea5e9"], published: ["Published", "#16a34a"],
  rejected: ["Turned down", "#dc2626"], hidden: ["Hidden (flagged)", "#d97706"], removed: ["Removed", "#71717a"],
};

async function api(method, url, body) {
  const res = await fetch(url, { method, credentials: "include", headers: body ? { "Content-Type": "application/json" } : {}, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const reportHref = (s) => (s.reportId ? `#/kb/e/${encodeURIComponent(s.reportId)}` : null);

function StatusChip({ status, ui }) {
  const [label, color] = STATUS[status] || [status, "#71717a"];
  return <Chip ui={ui} color={color}>{label}</Chip>;
}

function Who({ c, ui }) {
  const { B, fs } = ui;
  if (!c) return null;
  return (
    <a href={`https://github.com/${encodeURIComponent(c.login)}`} target="_blank" rel="noreferrer noopener" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: B.textMuted, textDecoration: "none", fontSize: fs(12) }} title={TIER_LABEL[c.tier]}>
      {c.avatar ? <img src={c.avatar} alt="" width={18} height={18} style={{ borderRadius: "50%" }} /> : null}
      {c.login}{c.tier !== "new" && <span style={{ color: B.textDim }}>· {c.tier}</span>}
    </a>
  );
}

function Counts({ s, ui }) {
  const { B, fs } = ui;
  const c = s.counts;
  if (!c) return null;
  const bits = [[c.iocs, "indicator"], [c.techniques, "technique"], [c.cves, "CVE"], [c.named, "group or software named", "groups or software named"]].filter(([n]) => n);
  return <span style={{ fontSize: fs(11), color: B.textDim }}>{bits.map(([n, one, many]) => `${nf(n)} ${n === 1 ? one : many || `${one}s`}`).join(" · ")}</span>;
}

// ─── Crowdsourced Reports ───

function ReportsTab({ me, ui, onFlagged }) {
  const { B, fs, HP } = ui;
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const load = useCallback((offset = 0) => {
    setLoading(true);
    api("GET", `/api/community/submissions?limit=30&offset=${offset}${query ? `&q=${encodeURIComponent(query)}` : ""}`)
      .then(d => { setItems(prev => (offset ? [...prev, ...d.items] : d.items)); setTotal(d.total); setError(""); })
      .catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [query]);
  useEffect(() => { load(0); }, [load]);
  const flag = async (s) => {
    const why = window.prompt(`Why should "${s.title}" be hidden? (Three flags hide it until the operator looks.)`);
    if (why === null) return;
    try { const d = await api("POST", `/api/community/submissions/${s.id}/flag`, { why }); setItems(list => list.map(x => (x.id === s.id ? d.submission : x)).filter(x => x.status === "published")); onFlagged?.(); }
    catch (e) { setError(e.message); }
  };
  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search titles, contributors, links…" aria-label="Search crowdsourced reports" style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <a href="#/kb/reports?source=community" style={{ fontSize: fs(12), color: B.accent }}>Also under Reports →</a>
      </div>
      {error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 8 }}>{error}</div>}
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 8 }}>{loading && !items.length ? "Loading…" : `${nf(total)} published`}</div>
      {!loading && !items.length && <div style={{ ...card(ui), padding: 20, fontSize: fs(13), color: B.textMuted }}>{query ? "Nothing matches." : "Nothing has been submitted yet. Be the first: open Submit."}</div>}
      {items.length > 0 && (
        <div style={{ ...card(ui), overflow: "hidden" }}>
          {items.map((s, i) => (
            <div key={s.id} style={{ padding: "12px 16px", borderTop: i ? `1px solid ${B.border}` : "none", display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Chip ui={ui} color="#8b5cf6">{KIND_LABEL[s.kind]}</Chip>
                <a href={reportHref(s) || "#/community"} style={{ flex: "1 1 260px", minWidth: 0, fontSize: fs(14), fontWeight: 700, color: B.text, textDecoration: "none", wordBreak: "break-word" }}>{s.title}</a>
                {me && me.login !== s.contributor?.login && (s.flagged
                  ? <span style={{ fontSize: fs(11), color: B.textDim }}>Flagged</span>
                  : <button onClick={() => flag(s)} style={{ ...HP.navBtn, padding: "4px 10px", fontSize: fs(11) }} title="Ask for it to be hidden: wrong, harmful or spam">Flag</button>)}
              </div>
              {s.note && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5 }}>{s.note.slice(0, 300)}{s.note.length > 300 ? "…" : ""}</div>}
              <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap", alignItems: "center", fontSize: fs(11), color: B.textDim }}>
                <Who c={s.contributor} ui={ui} />
                <span>{ago(s.publishedAt || s.createdAt)}</span>
                <Counts s={s} ui={ui} />
                {s.url && <a href={s.url} target="_blank" rel="noreferrer noopener nofollow" style={{ color: B.textDim, wordBreak: "break-all" }}>{s.url.replace(/^https?:\/\//, "").slice(0, 70)} ↗</a>}
              </div>
            </div>
          ))}
        </div>
      )}
      {items.length < total && <button onClick={() => load(items.length)} style={{ ...control(ui), cursor: "pointer", marginTop: 12 }}>Show more</button>}
    </div>
  );
}

// ─── Submit ───

function SignInCard({ info, ui, ret }) {
  const { B, fs, HP } = ui;
  return (
    <div style={{ ...card(ui), padding: "20px 22px", maxWidth: 680 }}>
      <div style={{ fontSize: fs(15), fontWeight: 800, color: B.text, marginBottom: 6 }}>Sign in with GitHub to contribute</div>
      <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6, marginBottom: 14 }}>
        Idle Hour only reads your public GitHub profile (your name, picture and when the account was made) to know who submitted what. No password or email is kept, and nothing is posted to GitHub.
      </div>
      {info?.signIn
        ? <a href={`/api/community/auth/github?return=${encodeURIComponent(ret)}`} style={{ ...HP.primaryBtn, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>Sign in with GitHub</a>
        : <div style={{ fontSize: fs(12), color: B.textDim }}>Sign-in isn't switched on yet. Check back soon.</div>}
    </div>
  );
}

function SubmitTab({ me, info, ui, onSubmitted }) {
  const { B, fs, HP } = ui;
  const [kind, setKind] = useState("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!me) return <SignInCard info={info} ui={ui} ret="#/community/submit" />;
  const lim = me.limits || {};
  const left = Math.max(0, (lim.perDay || 0) - (me.usedToday || 0));
  const readFile = (f) => {
    if (!f) return;
    if (f.size > (lim.mb || 2) * 1024 * 1024) { setError(`That file is over your ${lim.mb} MB limit.`); return; }
    const r = new FileReader();
    r.onload = () => { setText(String(r.result || "")); setFileName(f.name); setError(""); };
    r.readAsText(f);
  };
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await api("POST", "/api/community/submissions", { kind, url: kind === "url" ? url : undefined, text: kind === "url" ? undefined : text, title, note, public: isPublic });
      setUrl(""); setText(""); setFileName(""); setTitle(""); setNote(""); setIsPublic(false);
      onSubmitted();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const pill = (on) => ({ ...HP.navBtn, padding: "7px 14px", fontSize: fs(13), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const ready = isPublic && left > 0 && (kind === "url" ? /^https?:\/\/\S+\.\S+/.test(url.trim()) : text.trim().length > 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 820 }}>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: 0, lineHeight: 1.65 }}>
        Submit an article's link, a STIX bundle or an IOC list and help enrich Idle Hour's community-driven threat intelligence. It's checked automatically (no AI: the same rules that read the report feeds) and, if it passes, it appears in Crowdsourced Reports within a few minutes. Follow every submission under My OSINT. Thanks for contributing!
      </p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} role="group" aria-label="What you're submitting">
        {Object.entries(KIND_LABEL).map(([k, l]) => <button key={k} onClick={() => { setKind(k); setError(""); }} style={pill(kind === k)} aria-pressed={kind === k}>{l}</button>)}
      </div>
      {kind === "url" ? (
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://… an article or report about a threat" aria-label="Link" style={{ ...control(ui), width: "100%", boxSizing: "border-box" }} />
      ) : (
        <div>
          <textarea value={text} onChange={e => { setText(e.target.value); setFileName(""); }} rows={9} placeholder={kind === "stix" ? "Paste a STIX 2.1 bundle (JSON), or choose a file below" : "Paste indicators: IPs, domains, URLs or file hashes, one per line (defanged is fine: evil[.]com), or choose a file below"} aria-label={KIND_LABEL[kind]} style={{ ...control(ui), width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: fs(12) }} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: fs(12), color: B.textMuted, marginTop: 6, cursor: "pointer" }}>
            <input type="file" accept={kind === "stix" ? ".json,application/json" : ".txt,.csv,.ioc,.json,text/plain"} onChange={e => readFile(e.target.files?.[0])} aria-label="Choose a file" />
            {fileName && <span>Loaded {fileName}</span>}
          </label>
        </div>
      )}
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder={kind === "url" ? "Title (optional: the page's own title is used)" : "Title, e.g. \"Lumma Stealer C2s seen this week\""} aria-label="Title" style={{ ...control(ui), width: "100%", boxSizing: "border-box" }} />
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Notes (optional): where it's from, what it's about, how sure you are" aria-label="Notes" style={{ ...control(ui), width: "100%", boxSizing: "border-box" }} />
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: fs(12), color: B.textMuted, lineHeight: 1.5, cursor: "pointer" }}>
        <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} style={{ marginTop: 3 }} />
        <span>It's public (TLP:CLEAR) and I may share it. Everything submitted here is published on the open web under my GitHub name.</span>
      </label>
      {error && <div role="alert" style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={submit} disabled={!ready || busy} style={{ ...HP.primaryBtn, opacity: !ready || busy ? 0.5 : 1, cursor: !ready || busy ? "default" : "pointer" }}>{busy ? "Sending…" : "Submit"}</button>
        <span style={{ fontSize: fs(11), color: B.textDim }}>{left} of {lim.perDay} left today</span>
      </div>
      <div style={{ ...card(ui), padding: "12px 16px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6 }}>
        <div style={{ ...sectionLabel(ui), marginBottom: 4 }}>Your limits</div>
        As a {TIER_LABEL[me.tier]?.toLowerCase()}: {lim.perDay} submissions a day, up to {nf(lim.iocs)} indicators and {lim.mb} MB each.
        {me.tier === "new" && <> They grow once {info?.established?.accepted || 3} of your submissions are accepted with none taken down, on a GitHub account at least {info?.established?.githubDays || 30} days old.</>}
        {" "}Popular legitimate sites, private addresses and public DNS resolvers are taken out of any list, and a list that's mostly such is turned down.
      </div>
    </div>
  );
}

// ─── My OSINT ───

function Submission({ s, ui, admin, onAction }) {
  const { B, fs, HP } = ui;
  const [open, setOpen] = useState(false);
  return (
    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <StatusChip status={s.status} ui={ui} />
        <Chip ui={ui} color="#8b5cf6">{KIND_LABEL[s.kind]}</Chip>
        {reportHref(s) && s.status === "published"
          ? <a href={reportHref(s)} style={{ flex: "1 1 240px", minWidth: 0, fontSize: fs(14), fontWeight: 700, color: B.text, textDecoration: "none", wordBreak: "break-word" }}>{s.title || s.url}</a>
          : <span style={{ flex: "1 1 240px", minWidth: 0, fontSize: fs(14), fontWeight: 700, color: B.text, wordBreak: "break-word" }}>{s.title || s.url || "Untitled"}</span>}
        <span style={{ fontSize: fs(11), color: B.textDim }}>{ago(s.createdAt)}</span>
      </div>
      {s.reason && <div style={{ fontSize: fs(12), color: s.status === "rejected" ? "#dc2626" : B.textMuted }}>{s.reason}</div>}
      <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap", alignItems: "center", fontSize: fs(11), color: B.textDim }}>
        {admin && <Who c={s.contributor} ui={ui} />}
        <Counts s={s} ui={ui} />
        {s.flags > 0 && <span>{s.flags} flag{s.flags === 1 ? "" : "s"}</span>}
        {(s.checks?.length > 0 || s.dropped?.length > 0) && <button onClick={() => setOpen(o => !o)} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", padding: 0, fontSize: fs(11) }} aria-expanded={open}>{open ? "Hide the checks" : `What was checked${s.dropped?.length ? ` · ${s.dropped.length} dropped` : ""}`}</button>}
        {onAction && s.status === "hidden" && <button onClick={() => onAction(s, "unhide")} style={{ ...HP.navBtn, padding: "3px 9px", fontSize: fs(11) }}>Unhide</button>}
        {onAction && ["published", "hidden"].includes(s.status) && <button onClick={() => window.confirm(`Remove "${s.title}"? It counts as a takedown on ${s.contributor?.login}'s record.`) && onAction(s, "remove")} style={{ ...HP.navBtn, padding: "3px 9px", fontSize: fs(11), color: "#dc2626" }}>Remove</button>}
      </div>
      {open && (
        <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.6, paddingLeft: 4 }}>
          {(s.checks || []).map((c, i) => <div key={i}>{c.ok ? "✓" : "✗"} {c.label}{c.detail ? `: ${c.detail}` : ""}</div>)}
          {s.dropped?.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ color: B.textDim }}>Dropped:</div>
              {s.dropped.slice(0, 30).map((d, i) => <div key={i} style={{ fontFamily: "monospace", fontSize: fs(11), wordBreak: "break-all" }}>{d.value} <span style={{ fontFamily: "'Satoshi', sans-serif", color: B.textDim }}>({d.why})</span></div>)}
              {s.dropped.length > 30 && <div style={{ color: B.textDim }}>and {s.dropped.length - 30} more</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MineTab({ me, info, ui, stamp }) {
  const { B, fs } = ui;
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => (me ? api("GET", "/api/community/submissions/mine").then(d => setList(d.submissions)).catch(e => setError(e.message)) : null), [me]);
  useEffect(() => { load(); }, [load, stamp]);
  const busy = (list || []).some(s => s.status === "queued" || s.status === "checking");
  useEffect(() => { if (!busy) return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [busy, load]);
  if (!me) return <SignInCard info={info} ui={ui} ret="#/community/mine" />;
  return (
    <div>
      {error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 8 }}>{error}</div>}
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 8 }}>
        {list ? `${nf(list.length)} submission${list.length === 1 ? "" : "s"} · ${nf(me.accepted)} accepted` : "Loading…"}{busy ? " · checking…" : ""}
      </div>
      {list && !list.length && <div style={{ ...card(ui), padding: 20, fontSize: fs(13), color: B.textMuted }}>Nothing yet. What you submit shows up here while it's checked, with the reason if it's turned down.</div>}
      {list?.length > 0 && <div style={{ ...card(ui), overflow: "hidden" }}>{list.map((s, i) => <div key={s.id} style={{ borderTop: i ? `1px solid ${B.border}` : "none" }}><Submission s={s} ui={ui} /></div>)}</div>}
    </div>
  );
}

// ─── Review (the operator) ───

function ReviewTab({ ui }) {
  const { B, fs, HP } = ui;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api("GET", "/api/community/admin").then(setData).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (s, action) => { try { await api("POST", `/api/community/admin/submissions/${s.id}`, { action }); load(); } catch (e) { setError(e.message); } };
  const actOn = async (c, action) => { try { await api("POST", `/api/community/admin/contributors/${encodeURIComponent(c.id)}`, { action }); load(); } catch (e) { setError(e.message); } };
  if (error) return <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>;
  if (!data) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  const box = (title, children) => <div style={{ marginBottom: 18 }}><div style={{ ...sectionLabel(ui), marginBottom: 8 }}>{title}</div>{children}</div>;
  const list = (items, empty) => (items.length ? <div style={{ ...card(ui), overflow: "hidden" }}>{items.map((s, i) => <div key={s.id} style={{ borderTop: i ? `1px solid ${B.border}` : "none" }}><Submission s={s} ui={ui} admin onAction={act} />{s.flagsDetail?.length > 0 && <div style={{ padding: "0 16px 10px", fontSize: fs(11), color: B.textDim }}>{s.flagsDetail.map((f, j) => <div key={j}>{f.login}: {f.why || "(no reason)"}</div>)}</div>}</div>)}</div> : <div style={{ ...card(ui), padding: 16, fontSize: fs(12), color: B.textMuted }}>{empty}</div>);
  return (
    <div>
      {box(`Hidden by flags (${data.hidden.length})`, list(data.hidden, "Nothing is waiting for you."))}
      {box("Latest submissions", list(data.recent, "None yet."))}
      {box(`Contributors (${data.contributors.length})`, (
        <div style={{ ...card(ui), overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560, fontSize: fs(12) }}>
            <thead><tr style={{ textAlign: "left", color: B.textDim, fontSize: fs(10), textTransform: "uppercase", letterSpacing: 1 }}>{["Contributor", "Tier", "Accepted", "Turned down", "Takedowns", ""].map(h => <th key={h} style={{ padding: "8px 12px" }}>{h}</th>)}</tr></thead>
            <tbody>
              {data.contributors.map(c => (
                <tr key={c.id} style={{ borderTop: `1px solid ${B.border}` }}>
                  <td style={{ padding: "8px 12px" }}><Who c={c} ui={ui} /></td>
                  <td style={{ padding: "8px 12px", color: B.textMuted }}>{c.tier}</td>
                  <td style={{ padding: "8px 12px" }}>{nf(c.accepted)}</td>
                  <td style={{ padding: "8px 12px" }}>{nf(c.rejected)}</td>
                  <td style={{ padding: "8px 12px" }}>{nf(c.takedowns)}</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    <button onClick={() => actOn(c, c.trusted ? "untrust" : "trust")} style={{ ...HP.navBtn, padding: "3px 9px", fontSize: fs(11), marginRight: 6 }}>{c.trusted ? "Untrust" : "Trust"}</button>
                    <button onClick={() => (c.banned || window.confirm(`Ban ${c.login}? Their published submissions are removed.`)) && actOn(c, c.banned ? "unban" : "ban")} style={{ ...HP.navBtn, padding: "3px 9px", fontSize: fs(11), color: c.banned ? undefined : "#dc2626" }}>{c.banned ? "Unban" : "Ban"}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── The page ───

export function CommunityIntelPage({ onNavigate, user, onLogin, onLogout, pcapSession, collapsed, onToggleCollapse, ui }) {
  const { B, fs, HP, AppShell } = ui;
  const [tab, setTab] = useState(() => parseCommunityHash()?.tab || "reports");
  const [info, setInfo] = useState(null);
  const [stamp, setStamp] = useState(0);
  const me = info?.contributor || null;
  const loadMe = useCallback(() => api("GET", "/api/community/me").then(setInfo).catch(() => setInfo({ signIn: false, contributor: null })), []);
  useEffect(() => { loadMe(); }, [loadMe, stamp]);
  useEffect(() => {
    const onHash = () => { const r = parseCommunityHash(); if (r) setTab(r.tab); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const go = (t) => { setTab(t); window.history.replaceState(null, "", t === "reports" ? "#/community" : `#/community/${t}`); };
  const tabs = useMemo(() => TABS.filter(([k]) => k !== "review" || me?.admin), [me]);
  const signOut = async () => { await api("POST", "/api/community/auth/logout").catch(() => {}); setStamp(s => s + 1); };
  const tabBtn = (on) => ({ background: "none", border: "none", borderBottom: `2px solid ${on ? B.primary : "transparent"}`, color: on ? B.text : B.textDim, padding: "10px 14px", cursor: "pointer", fontSize: fs(13), fontWeight: on ? 700 : 500, fontFamily: "'Satoshi', sans-serif", whiteSpace: "nowrap" });

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <AppShell page="community" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, minWidth: 0, overflowY: "auto", overflowX: "hidden", background: B.bg, color: B.text, fontFamily: "'Satoshi', sans-serif", padding: "28px clamp(16px, 4vw, 40px) 60px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
            <h1 style={{ fontSize: fs(28), fontWeight: 900, margin: 0 }}>Community Intel</h1>
            {me ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: fs(12), color: B.textMuted }}>
                <Who c={me} ui={ui} />
                <Chip ui={ui} color={me.tier === "new" ? "#a1a1aa" : "#16a34a"}>{TIER_LABEL[me.tier]}</Chip>
                <button onClick={signOut} style={{ ...HP.navBtn, padding: "5px 11px", fontSize: fs(12) }}>Sign out</button>
              </div>
            ) : info?.signIn ? (
              <a href={`/api/community/auth/github?return=${encodeURIComponent(tab === "reports" ? "#/community" : `#/community/${tab}`)}`} style={{ ...HP.navBtn, padding: "7px 13px", fontSize: fs(12), textDecoration: "none", color: B.text }}>Sign in with GitHub</a>
            ) : null}
          </div>
          <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 14px", maxWidth: "100ch", lineHeight: 1.6 }}>
            Threat intelligence submitted by the community: article links, STIX bundles and IOC lists, checked by rules and published within minutes. Each is also one of the Reports, and its indicators join the Indicators lists, scored lower than the curated feeds.
          </p>
          <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${B.border}`, marginBottom: 18, overflowX: "auto" }} role="tablist">
            {tabs.map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => go(k)} style={tabBtn(tab === k)}>{l}</button>)}
          </div>
          {tab === "reports" && <ReportsTab me={me} ui={ui} />}
          {tab === "submit" && <SubmitTab me={me} info={info} ui={ui} onSubmitted={() => { setStamp(s => s + 1); go("mine"); }} />}
          {tab === "mine" && <MineTab me={me} info={info} ui={ui} stamp={stamp} />}
          {tab === "review" && (me?.admin ? <ReviewTab ui={ui} /> : <div style={{ color: B.textDim, fontSize: fs(13) }}>Only the operator can see this.</div>)}
        </div>
      </div>
    </div>
  );
}
